'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '../utils/cn';
import { getChartColors } from '../utils/chart-theme';

// ─── Types ────────────────────────────────────────────────────────────────────

export type HeatmapCell = {
  label: string;
  value: number;
  axisLabel?: string;
};

export type HeatmapGridRow = {
  id: string;
  /** Y-axis label (e.g. day-of-month or week start). */
  label: string;
  values: number[];
};

export type ActivityHeatmapGridProps = {
  rows: HeatmapGridRow[];
  /** Column labels aligned to values length; use empty string for hidden ticks. */
  columnLabels?: string[];
  unit?: string;
  color?: string;
  periodLabel?: string;
  onPrevPeriod?: () => void;
  onNextPeriod?: () => void;
  canGoNext?: boolean;
  showLegend?: boolean;
  /** When false, period nav must be rendered externally (e.g. in a panel header). */
  showNav?: boolean;
  /** Square cell edge length in px. Same value keeps both heatmaps visually consistent. */
  cellSize?: number;
  /** Minimum data rows to reserve (prevents height jump when period has fewer rows). */
  minRows?: number;
  className?: string;
};

/** Default square cell size for 2-D activity heatmaps. */
export const HEATMAP_GRID_CELL_SIZE = 24;

/** Minimum data rows reserved in the grid so layout height stays stable across periods. */
export const HEATMAP_GRID_MIN_ROWS = 8;

const HEATMAP_GRID_GAP = 2;
const HEATMAP_X_AXIS_HEIGHT = 14;

function heatmapDisplayRowCount(rowCount: number, minRows = HEATMAP_GRID_MIN_ROWS): number {
  return Math.max(rowCount, minRows);
}

function heatmapGridMinHeight(
  rowCount: number,
  cellSize: number,
  hasColumnLabels: boolean,
  minRows = HEATMAP_GRID_MIN_ROWS
): number {
  const displayRows = heatmapDisplayRowCount(rowCount, minRows);
  const bodyHeight = displayRows * cellSize + (displayRows - 1) * HEATMAP_GRID_GAP;
  if (!hasColumnLabels) return bodyHeight;
  return bodyHeight + HEATMAP_GRID_GAP + HEATMAP_X_AXIS_HEIGHT;
}

type ActivityHeatmapProps = {
  cells: HeatmapCell[];
  unit?: string;
  color?: string;
  minCellSize?: number;
  showLegend?: boolean;
  className?: string;
};

// ─── Date helpers (exported for heatmap period navigation) ────────────────────

export function currentMonthKey(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function parseMonthKey(key: string): { year: number; month: number } {
  const [y, m] = key.split('-').map(Number);
  return { year: y!, month: m! };
}

export function shiftMonthKey(key: string, delta: number): string {
  const { year, month } = parseMonthKey(key);
  const d = new Date(Date.UTC(year, month - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

export function formatMonthLabel(key: string): string {
  const { year, month } = parseMonthKey(key);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(year, month - 1, 1))
  );
}

export function formatTwoMonthLabel(startMonthKey: string): string {
  const { year, month } = parseMonthKey(startMonthKey);
  const end = new Date(Date.UTC(year, month - 1 + 1, 1));
  const startFmt = new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
    new Date(Date.UTC(year, month - 1, 1))
  );
  const endFmt = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(end);
  return `${startFmt} – ${endFmt}`;
}

export function twoMonthRange(startMonthKey: string): { start: string; end: string } {
  const { year, month } = parseMonthKey(startMonthKey);
  const start = `${startMonthKey}-01`;
  const endDate = new Date(Date.UTC(year, month - 1 + 2, 0));
  return { start, end: endDate.toISOString().slice(0, 10) };
}

export function defaultTwoMonthStart(): string {
  return shiftMonthKey(currentMonthKey(), -1);
}

export function shiftDateKey(iso: string, deltaDays: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** Sunday UTC of the week containing `iso` (or today). */
export function weekStartSunday(iso?: string): string {
  const d = iso ? new Date(`${iso}T00:00:00.000Z`) : new Date();
  const sunday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  sunday.setUTCDate(sunday.getUTCDate() - sunday.getUTCDay());
  return sunday.toISOString().slice(0, 10);
}

export function shiftWeekStart(weekStart: string, deltaWeeks: number): string {
  return shiftDateKey(weekStart, deltaWeeks * 7);
}

export function formatWeekLabel(weekStart: string): string {
  const weekEnd = shiftDateKey(weekStart, 6);
  try {
    const start = new Date(`${weekStart}T00:00:00.000Z`);
    const end = new Date(`${weekEnd}T00:00:00.000Z`);
    const fmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    const endFmt = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return `${fmt.format(start)} – ${endFmt.format(end)}`;
  } catch {
    return `${weekStart} – ${weekEnd}`;
  }
}

// ─── Shared color ramp ────────────────────────────────────────────────────────

function hexToRgba(hex: string, alpha: number): string | null {
  const m = hex.replace('#', '');
  if (m.length !== 3 && m.length !== 6) return null;
  const full = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some(n => Number.isNaN(n))) return null;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const LEVEL_ALPHA = [0, 0.22, 0.45, 0.7, 1] as const;

function levelFor(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  return Math.min(4, Math.ceil((value / max) * 4));
}

function useHeatmapRamp(color?: string) {
  const themeColors = getChartColors();
  const accent = color ?? themeColors.primary;

  const fillFor = React.useCallback(
    (level: number): string => {
      if (level === 0) return 'transparent';
      return hexToRgba(accent, LEVEL_ALPHA[level] ?? 1) ?? accent;
    },
    [accent]
  );

  return { accent, fillFor };
}

// ─── Legend ───────────────────────────────────────────────────────────────────

const HeatmapLegend = ({ fillFor }: { fillFor: (level: number) => string }) => (
  <div className="flex items-center justify-end gap-1 pt-0.5">
    <span className="text-[9px] text-muted-foreground/50">Less</span>
    {LEVEL_ALPHA.map((_, level) => (
      <span
        key={level}
        className={cn(
          'w-2.5 h-2.5 rounded-[2px] border',
          level === 0 ? 'border-border/30 bg-muted/20' : 'border-transparent'
        )}
        style={level === 0 ? undefined : { backgroundColor: fillFor(level) }}
      />
    ))}
    <span className="text-[9px] text-muted-foreground/50">More</span>
  </div>
);

// ─── Period navigation ────────────────────────────────────────────────────────

export const HeatmapPeriodNav = ({
  label,
  onPrev,
  onNext,
  canGoNext,
  className,
}: {
  label: string;
  onPrev?: () => void;
  onNext?: () => void;
  canGoNext?: boolean;
  className?: string;
}) => (
  <div className={cn('flex items-center gap-1 flex-shrink-0', className)}>
    <button
      type="button"
      onClick={onPrev}
      disabled={!onPrev}
      title="Previous period"
      className={cn(
        'p-1 rounded-lg text-muted-foreground transition-colors',
        'hover:text-foreground hover:bg-muted/60',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        'disabled:opacity-30 disabled:pointer-events-none'
      )}
    >
      <ChevronLeft size={14} />
    </button>
    <span className="min-w-[88px] text-center text-[10px] font-medium text-foreground tabular-nums">
      {label}
    </span>
    <button
      type="button"
      onClick={onNext}
      disabled={!onNext || canGoNext === false}
      title="Next period"
      className={cn(
        'p-1 rounded-lg text-muted-foreground transition-colors',
        'hover:text-foreground hover:bg-muted/60',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        'disabled:opacity-30 disabled:pointer-events-none'
      )}
    >
      <ChevronRight size={14} />
    </button>
  </div>
);

// ─── 1-D strip ────────────────────────────────────────────────────────────────

export const ActivityHeatmap = ({
  cells,
  unit = 'events',
  color,
  minCellSize = 14,
  showLegend = true,
  className,
}: ActivityHeatmapProps) => {
  const { fillFor } = useHeatmapRamp(color);
  const max = React.useMemo(() => Math.max(...cells.map(c => c.value), 0), [cells]);
  const hasAxisLabels = cells.some(c => c.axisLabel);

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `repeat(${cells.length}, minmax(${minCellSize}px, 1fr))`,
    gap: '3px',
  };

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div style={gridStyle}>
        {cells.map((cell, i) => {
          const level = levelFor(cell.value, max);
          const tooltip = `${cell.label} · ${cell.value.toLocaleString()} ${unit}`;
          return (
            <div
              key={`${cell.label}-${i}`}
              title={tooltip}
              aria-label={tooltip}
              className={cn(
                'aspect-square rounded-[3px] border transition-colors',
                level === 0 ? 'border-border/30 bg-muted/20' : 'border-transparent'
              )}
              style={level === 0 ? undefined : { backgroundColor: fillFor(level) }}
            />
          );
        })}
      </div>

      {hasAxisLabels && (
        <div style={gridStyle}>
          {cells.map((cell, i) => (
            <span
              key={`axis-${cell.label}-${i}`}
              className="text-[8px] text-muted-foreground/60 text-center tabular-nums truncate"
            >
              {cell.axisLabel ?? ''}
            </span>
          ))}
        </div>
      )}

      {showLegend && <HeatmapLegend fillFor={fillFor} />}
    </div>
  );
};

ActivityHeatmap.displayName = 'ActivityHeatmap';

// ─── 2-D grid ─────────────────────────────────────────────────────────────────

/**
 * ActivityHeatmapGrid — a 2-D GitHub-style intensity grid.
 *
 * Rows map to the Y axis (days in a month, weeks in a quarter, etc.).
 * Columns map to the X axis (hours, days-of-week, etc.).
 * Optional period navigation arrows sit above the grid.
 */
export const ActivityHeatmapGrid = ({
  rows,
  columnLabels,
  unit = 'events',
  color,
  periodLabel,
  onPrevPeriod,
  onNextPeriod,
  canGoNext,
  showLegend = true,
  showNav = true,
  cellSize = HEATMAP_GRID_CELL_SIZE,
  minRows = HEATMAP_GRID_MIN_ROWS,
  className,
}: ActivityHeatmapGridProps) => {
  const { fillFor } = useHeatmapRamp(color);
  const colCount = rows[0]?.values.length ?? 0;
  const gap = HEATMAP_GRID_GAP;

  const max = React.useMemo(
    () => Math.max(...rows.flatMap(r => r.values), 0),
    [rows]
  );

  if (colCount === 0) {
    return (
      <div className={cn('text-xs text-muted-foreground/60 py-4 text-center w-full', className)}>
        No activity in this period.
      </div>
    );
  }

  const displayRowCount = heatmapDisplayRowCount(rows.length, minRows);
  const rowOffset = displayRowCount - rows.length;
  const xAxisRow = displayRowCount + 1;
  const gridMinHeight = heatmapGridMinHeight(rows.length, cellSize, !!columnLabels, minRows);

  const cellStyle = { width: cellSize, height: cellSize, minWidth: cellSize, minHeight: cellSize };

  return (
    <div className={cn('w-full', className)}>
      {showNav && (periodLabel || onPrevPeriod) && (
        <HeatmapPeriodNav
          label={periodLabel ?? ''}
          onPrev={onPrevPeriod}
          onNext={onNextPeriod}
          canGoNext={canGoNext}
          className="justify-end mb-2"
        />
      )}

      {/* Grid is only as wide as its data; min height reserves space for minRows */}
      <div className="w-full flex flex-col justify-end" style={{ minHeight: gridMinHeight }}>
        <div
          className="inline-grid w-fit max-w-full"
          style={{
            gap: `${gap}px`,
            gridTemplateColumns: `2rem repeat(${colCount}, ${cellSize}px)`,
            gridTemplateRows: `repeat(${displayRowCount}, ${cellSize}px)${
              columnLabels ? ` ${HEATMAP_X_AXIS_HEIGHT}px` : ''
            }`,
          }}
        >
          {rows.map((row, rowIdx) => {
            const gridRow = rowOffset + rowIdx + 1;
            return (
            <React.Fragment key={row.id}>
              <span
                className="text-[8px] text-muted-foreground/60 text-right tabular-nums leading-none flex items-center justify-end pr-0.5 truncate"
                style={{ gridColumn: 1, gridRow, height: cellSize }}
                title={row.label}
              >
                {row.label}
              </span>
              {row.values.map((value, colIdx) => {
                const level = levelFor(value, max);
                const colLabel = columnLabels?.[colIdx] ?? String(colIdx);
                const tooltip = `${row.label} ${colLabel} · ${value.toLocaleString()} ${unit}`;
                return (
                  <div
                    key={`${row.id}-${colIdx}`}
                    title={tooltip}
                    aria-label={tooltip}
                    className={cn(
                      'rounded-[2px] border transition-colors',
                      level === 0 ? 'border-border/25 bg-muted/15' : 'border-transparent'
                    )}
                    style={{
                      ...cellStyle,
                      gridColumn: colIdx + 2,
                      gridRow,
                      ...(level === 0 ? {} : { backgroundColor: fillFor(level) }),
                    }}
                  />
                );
              })}
            </React.Fragment>
            );
          })}

          {columnLabels && (
            <>
              <span style={{ gridColumn: 1, gridRow: xAxisRow }} />
              {columnLabels.map((label, i) => (
                <span
                  key={`xlabel-${i}`}
                  className="text-[8px] text-muted-foreground/60 text-center tabular-nums truncate flex items-start justify-center"
                  style={{
                    gridColumn: i + 2,
                    gridRow: xAxisRow,
                    width: cellSize,
                  }}
                >
                  {label}
                </span>
              ))}
            </>
          )}
        </div>
      </div>

      {showLegend && <HeatmapLegend fillFor={fillFor} />}
    </div>
  );
};

ActivityHeatmapGrid.displayName = 'ActivityHeatmapGrid';
